<template>
  <v-dialog v-model="showDialog" width="450" persistent>
    <v-card>
      <v-form ref="form" validate-on="invalid-input lazy" @submit.prevent="save">
        <v-card-title>{{ t("categories.manage.editCategoryTitle") }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.trim="name"
            :rules="[required, uniqueName]"
            variant="outlined"
            autofocus
          >
            <template #label>
              {{ t("categories.name") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <v-text-field
            v-model.number="amount"
            :rules="[required, positiveNumber]"
            type="number"
            min="1"
            variant="outlined"
          >
            <template #label>
              {{ t("categories.targetAmount") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showDialog = false">{{ t("common.cancel") }}</v-btn>
          <v-btn variant="flat" type="submit" color="primary">{{ t("common.save") }}</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";

const props = defineProps<{
  category: { id: string; name: string; amount: number } | null;
  existingCategories: Array<{ id: string; name: string }>;
}>();
const emit = defineEmits<{ save: [value: { name: string; amount: number }] }>();

const showDialog = defineModel<boolean>({ required: true });

const { t } = useI18n();
const { required, positiveNumber } = useRules();
const form = ref<VForm>();

const name = ref("");
const amount = ref<number | null>(null);

watch(
  () => [showDialog.value, props.category] as const,
  ([open, cat]) => {
    if (open) {
      name.value = cat?.name ?? "";
      amount.value = cat?.amount ?? null;
    }
  },
);

const uniqueName = computed(() => {
  return (value: string) => {
    if (!value) return true;
    const normalized = value.toLowerCase().trim();
    const conflict = props.existingCategories.find(
      (c) => c.name.toLowerCase().trim() === normalized && c.id !== props.category?.id,
    );
    return !conflict || t("categories.categoryAlreadyExists");
  };
});

const save = async () => {
  const valid = await form.value?.validate();
  if (!valid?.valid) return;
  if (amount.value === null) return;
  emit("save", { name: name.value, amount: amount.value });
  showDialog.value = false;
};
</script>
